import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "@/lib/router-compat";
import PageMeta from "@/components/PageMeta";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { ArrowRight, Calculator, Gauge, Info, MessageCircle } from "lucide-react";
import { trackEvent } from "@/lib/analytics";
import RelatedTools from "@/components/tools/RelatedTools";
import {
  BANDS,
  DEFAULT_INPUTS,
  DEROGATORY_OPTIONS,
  EXAMPLES,
  FACTORS,
  FAQS,
  calculateReadiness,
  type DerogatoryLevel,
  type ReadinessInputs,
} from "@/components/tools/readinessScoreData";
import { LAST_REVIEWED, PAGE_URL } from "@/data/homebuyerReadinessScoreSchema";



const CONTENTS = [
  { id: "factors", label: "The five factors" },
  { id: "formula", label: "A sample formula you can see" },
  { id: "bands", label: "Score bands" },
  { id: "examples", label: "Three worked examples" },
  { id: "calculator", label: "Calculate your score" },
  { id: "methodology", label: "Methodology and limits" },
  { id: "faq", label: "Common questions" },
];

const creditBucket = (s: number) =>
  s >= 760 ? "760+" : s >= 700 ? "700-759" : s >= 660 ? "660-699" : s >= 620 ? "620-659" : "under-620";
const dtiBucket = (d: number) =>
  d <= 28 ? "under-28" : d <= 36 ? "28-36" : d <= 43 ? "36-43" : d <= 50 ? "43-50" : "over-50";
const reserveBucket = (m: number) => (m >= 6 ? "6-plus" : m >= 3 ? "3-5" : m >= 1 ? "1-2" : "none");

const HomebuyerReadinessScore = () => {
  const [inputs, setInputs] = useState<ReadinessInputs>(DEFAULT_INPUTS);
  const [openFaq, setOpenFaq] = useState<string>("");
  const started = useRef(false);
  const lastBand = useRef<string>("");

  const result = useMemo(() => calculateReadiness(inputs), [inputs]);

  useEffect(() => {
    trackEvent("readiness_guide_view", { entry_section: "page_load" });
  }, []);

  useEffect(() => {
    if (!started.current) return;
    if (lastBand.current === result.band) return;
    lastBand.current = result.band;
    trackEvent("readiness_score_calculated", {
      score_band: result.band,
      credit_bucket: creditBucket(inputs.creditScore),
      dti_bucket: dtiBucket(result.dti),
      reserve_bucket: reserveBucket(inputs.reserveMonths),
    });
  }, [result.band, result.dti, inputs.creditScore, inputs.reserveMonths]);

  const update = <K extends keyof ReadinessInputs>(key: K, value: ReadinessInputs[K]) => {
    if (!started.current) {
      started.current = true;
      trackEvent("readiness_calc_start", { section_name: "calculator" });
    }
    setInputs((prev) => ({ ...prev, [key]: value }));
  };

  const num = (v: string, fallback = 0) => {
    const n = Number(v.replace(/[^0-9.]/g, ""));
    return Number.isFinite(n) ? n : fallback;
  };

  const handleCtaClick = (cta: string) =>
    trackEvent("readiness_result_cta_click", {
      option_type: cta,
      score_band: result.band,
      credit_bucket: creditBucket(inputs.creditScore),
      dti_bucket: dtiBucket(result.dti),
      reserve_bucket: reserveBucket(inputs.reserveMonths),
    });

  const handleFaqChange = (value: string) => {
    setOpenFaq(value);
    if (value) trackEvent("faq_open", { entry_section: "faq", option_type: value });
  };



  return (
    <div className="min-h-screen bg-background flex flex-col">
      <PageMeta
        title="What Is a Homebuyer Readiness Score? Factors + Free Calculator"
        description="A homebuyer readiness score combines credit, debt-to-income, cash, financial history, and employment into one number. See the five factors, a sample weighted formula, score bands, worked examples, and calculate your own estimate."
        path="/tools/homebuyer-readiness-score"
      />
      <Header />

      <main className="flex-1">
        <div className="container mx-auto px-4 py-8 sm:py-12 max-w-4xl">
          <Card className="mb-6 border-primary/30 bg-primary/5">
            <CardContent className="p-5 sm:p-6">
              <p className="mb-2 text-xs font-bold uppercase tracking-wide text-primary">Short answer</p>
              <p className="text-base leading-relaxed text-foreground sm:text-lg">
                A homebuyer readiness score is a single 0 to 100 estimate of how prepared you are to get a mortgage,
                built from five inputs: <strong>credit score, debt-to-income ratio, financial history, down payment
                and reserves, and employment stability</strong>. It is an educational summary, not a credit score and
                not a lending decision — and the models behind branded readiness scores are proprietary, so numbers
                are not comparable between tools.
              </p>
            </CardContent>
          </Card>

          <h1 className="mb-4 text-3xl font-bold tracking-tight text-foreground sm:text-4xl">
            What Is a Homebuyer Readiness Score?
          </h1>
          <p className="mb-3 text-base text-muted-foreground sm:text-lg">
            What goes into the score, how the math typically works, what each band means, and a calculator that shows
            you which single factor is costing you the most points right now.
          </p>
          <p className="mb-8 text-xs text-muted-foreground">
            Last reviewed: {LAST_REVIEWED}. Educational information only. Qualification standards vary by lender,
            loan program, and state.
          </p>

          <nav
            aria-label="On this page"
            className="sticky top-16 z-20 mb-10 rounded-xl border border-border bg-card/95 p-5 shadow-sm backdrop-blur print:hidden"
          >
            <p className="mb-3 text-sm font-bold text-foreground">On this page</p>
            <ul className="grid gap-x-6 gap-y-2 text-sm sm:grid-cols-2">
              {CONTENTS.map((c) => (
                <li key={c.id}>
                  <a href={`#${c.id}`} className="text-primary hover:underline">
                    {c.label}
                  </a>
                </li>
              ))}
            </ul>
          </nav>

          <section id="factors" className="mb-12 scroll-mt-24">
            <h2 className="mb-3 text-2xl font-bold text-foreground">The five factors</h2>
            <p className="mb-5 text-sm leading-relaxed text-muted-foreground">
              Readiness models differ in the details, but nearly all of them are built from the same five inputs —
              the things a lender evaluates when deciding whether, and on what terms, to lend.
            </p>
            <div className="space-y-4">
              {FACTORS.map((f) => (
                <Card key={f.id} className="border-l-4 border-l-primary">
                  <CardContent className="p-5">
                    <div className="mb-2 flex flex-wrap items-center gap-3">
                      <h3 className="font-bold text-foreground">{f.label}</h3>
                      <span className="rounded-full bg-primary/10 px-3 py-1 text-xs font-bold uppercase tracking-wide text-primary">
                        Sample weight: {f.weight}
                      </span>
                    </div>
                    <p className="mb-2 text-sm leading-relaxed text-muted-foreground">{f.what}</p>
                    <p className="text-sm leading-relaxed text-foreground">
                      <span className="font-semibold">What strong looks like: </span>
                      {f.strong}
                    </p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </section>

          <section id="formula" className="mb-12 scroll-mt-24">
            <h2 className="mb-3 text-2xl font-bold text-foreground">A sample formula you can see</h2>
            <p className="mb-5 text-sm leading-relaxed text-muted-foreground">
              Most published readiness scores do not show their math. Here is the weighting this page uses, stated
              openly so you can tell exactly why your number moves. Treat it as an educational illustration, not an
              industry standard.
            </p>
            <Card className="mb-4">
              <CardContent className="p-5">
                <ul className="mb-4 space-y-2">
                  {FACTORS.map((f) => (
                    <li key={f.id} className="flex items-center gap-3">
                      <span className="w-56 shrink-0 text-sm font-semibold text-foreground">{f.label}</span>
                      <div className="h-2 flex-1 overflow-hidden rounded-full bg-muted">
                        <div className="h-full rounded-full bg-primary" style={{ width: `${f.weight}%` }} />
                      </div>
                      <span className="w-12 shrink-0 text-right text-sm font-bold text-primary">{f.weight}</span>
                    </li>
                  ))}
                </ul>
                <p className="rounded-lg bg-muted/60 p-3 font-mono text-xs leading-relaxed text-foreground">
                  score = credit(0&ndash;35) + dti(0&ndash;25) + cash(0&ndash;20) + history(0&ndash;10) +
                  employment(0&ndash;10)
                </p>
              </CardContent>
            </Card>
            <Card className="border-primary/30 bg-primary/5">
              <CardContent className="flex gap-3 p-5">
                <Info className="mt-0.5 h-5 w-5 shrink-0 text-primary" aria-hidden />
                <div className="text-sm leading-relaxed text-foreground">
                  <p className="mb-1 font-bold">Guardrails</p>
                  <p>
                    Points alone do not tell the whole story. A bankruptcy or foreclosure within the last three years
                    caps the result at 64 regardless of how strong the other factors are, because program waiting
                    periods apply no matter how much cash or income you have. Scores also never exceed 100 or drop
                    below 0, and the cash factor splits into down payment (up to 12) and reserves (up to 8) so that
                    savings still count when the down payment percentage is small.
                  </p>
                </div>
              </CardContent>
            </Card>
          </section>

          <section id="bands" className="mb-12 scroll-mt-24">
            <h2 className="mb-3 text-2xl font-bold text-foreground">Score bands</h2>
            <div className="grid gap-4 sm:grid-cols-3">
              {BANDS.map((b) => (
                <Card key={b.id}>
                  <CardContent className="p-5">
                    <p className="text-xs font-bold uppercase tracking-wide text-primary">
                      {b.min}&ndash;{b.max}
                    </p>
                    <p className="mb-2 text-lg font-bold text-foreground">{b.label}</p>
                    <p className="text-sm leading-relaxed text-muted-foreground">{b.meaning}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </section>

          <section id="examples" className="mb-12 scroll-mt-24">
            <h2 className="mb-3 text-2xl font-bold text-foreground">Three worked examples</h2>
            <p className="mb-5 text-sm leading-relaxed text-muted-foreground">
              Same formula, three profiles. Each one shows the inputs, the score they produce, and the single change
              that would move that buyer up a tier.
            </p>
            <div className="space-y-4">
              {EXAMPLES.map((ex) => {
                const r = calculateReadiness(ex.inputs);
                return (
                  <Card key={ex.id}>
                    <CardContent className="p-5">
                      <div className="mb-3 flex flex-wrap items-center gap-3">
                        <h3 className="text-lg font-bold text-foreground">{ex.name}</h3>
                        <span className="rounded-full bg-primary px-3 py-1 text-xs font-bold uppercase tracking-wide text-primary-foreground">
                          {r.score} / 100 &middot; {r.bandLabel}
                        </span>
                      </div>
                      <dl className="mb-3 grid gap-x-6 gap-y-1 text-sm sm:grid-cols-2">
                        <div className="flex justify-between gap-2 border-b border-border py-1">
                          <dt className="text-muted-foreground">Credit score</dt>
                          <dd className="font-semibold text-foreground">{ex.inputs.creditScore}</dd>
                        </div>
                        <div className="flex justify-between gap-2 border-b border-border py-1">
                          <dt className="text-muted-foreground">Debt-to-income</dt>
                          <dd className="font-semibold text-foreground">{r.dti.toFixed(0)}%</dd>
                        </div>
                        <div className="flex justify-between gap-2 border-b border-border py-1">
                          <dt className="text-muted-foreground">Down payment</dt>
                          <dd className="font-semibold text-foreground">{ex.inputs.downPaymentPercent}%</dd>
                        </div>
                        <div className="flex justify-between gap-2 border-b border-border py-1">
                          <dt className="text-muted-foreground">Reserves</dt>
                          <dd className="font-semibold text-foreground">{ex.inputs.reserveMonths} months</dd>
                        </div>
                        <div className="flex justify-between gap-2 border-b border-border py-1">
                          <dt className="text-muted-foreground">Employment</dt>
                          <dd className="font-semibold text-foreground">{ex.inputs.employmentYears} years</dd>
                        </div>
                        <div className="flex justify-between gap-2 border-b border-border py-1">
                          <dt className="text-muted-foreground">Financial history</dt>
                          <dd className="font-semibold text-foreground">
                            {DEROGATORY_OPTIONS.find((o) => o.value === ex.inputs.derogatory)?.label}
                          </dd>
                        </div>
                      </dl>
                      <p className="text-sm leading-relaxed text-foreground">
                        <span className="font-semibold">Biggest single lever: </span>
                        {ex.lift}
                      </p>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </section>

          <section id="calculator" className="mb-12 scroll-mt-24">
            <div className="mb-3 flex items-center gap-2">
              <Calculator className="h-5 w-5 shrink-0 text-primary" aria-hidden />
              <h2 className="text-2xl font-bold text-foreground">Calculate your readiness score</h2>
            </div>
            <p className="mb-5 text-sm leading-relaxed text-muted-foreground">
              Everything recalculates as you type. Nothing is submitted, no credit is pulled, and no account is
              needed.
            </p>

            <div className="grid gap-6 lg:grid-cols-[1fr_20rem]">
              <Card>
                <CardContent className="grid gap-4 p-5 sm:grid-cols-2">
                  <div>
                    <Label htmlFor="credit">Credit score</Label>
                    <Input
                      id="credit"
                      inputMode="numeric"
                      value={inputs.creditScore}
                      onChange={(e) => update("creditScore", Math.min(850, num(e.target.value)))}
                    />
                    <p className="mt-1 text-xs text-muted-foreground">300&ndash;850</p>
                  </div>
                  <div>
                    <Label htmlFor="income">Gross monthly income ($)</Label>
                    <Input
                      id="income"
                      inputMode="numeric"
                      value={inputs.monthlyIncome}
                      onChange={(e) => update("monthlyIncome", num(e.target.value))}
                    />
                    <p className="mt-1 text-xs text-muted-foreground">Before taxes</p>
                  </div>
                  <div>
                    <Label htmlFor="debt">Monthly debt payments ($)</Label>
                    <Input
                      id="debt"
                      inputMode="numeric"
                      value={inputs.monthlyDebt}
                      onChange={(e) => update("monthlyDebt", num(e.target.value))}
                    />
                    <p className="mt-1 text-xs text-muted-foreground">
                      Include the future mortgage payment, cards, autos, and student loans
                    </p>
                  </div>
                  <div>
                    <Label htmlFor="dp">Down payment (% of price)</Label>
                    <Input
                      id="dp"
                      inputMode="decimal"
                      value={inputs.downPaymentPercent}
                      onChange={(e) => update("downPaymentPercent", Math.min(100, num(e.target.value)))}
                    />
                  </div>
                  <div>
                    <Label htmlFor="reserves">Reserve months after closing</Label>
                    <Input
                      id="reserves"
                      inputMode="numeric"
                      value={inputs.reserveMonths}
                      onChange={(e) => update("reserveMonths", Math.min(60, num(e.target.value)))}
                    />
                    <p className="mt-1 text-xs text-muted-foreground">
                      Months of housing payments left in the bank
                    </p>
                  </div>
                  <div>
                    <Label htmlFor="employment">Years at current job or field</Label>
                    <Input
                      id="employment"
                      inputMode="decimal"
                      value={inputs.employmentYears}
                      onChange={(e) => update("employmentYears", Math.min(50, num(e.target.value)))}
                    />
                  </div>
                  <div className="sm:col-span-2">
                    <Label htmlFor="derog">Major derogatory events</Label>
                    <Select
                      value={inputs.derogatory}
                      onValueChange={(v) => update("derogatory", v as DerogatoryLevel)}
                    >
                      <SelectTrigger id="derog">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {DEROGATORY_OPTIONS.map((o) => (
                          <SelectItem key={o.value} value={o.value}>
                            {o.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </CardContent>
              </Card>

              <div className="space-y-4">
                <Card className="border-primary/40 bg-primary/5">
                  <CardContent className="p-5 text-center">
                    <div className="mb-1 flex items-center justify-center gap-2">
                      <Gauge className="h-4 w-4 text-primary" aria-hidden />
                      <p className="text-xs font-bold uppercase tracking-wide text-primary">Estimated score</p>
                    </div>
                    <p className="text-5xl font-bold text-foreground" aria-live="polite">
                      {result.score}
                    </p>
                    <p className="mb-2 text-sm text-muted-foreground">out of 100</p>
                    <p className="inline-block rounded-full bg-primary px-4 py-1 text-sm font-bold text-primary-foreground">
                      {result.bandLabel}
                    </p>
                    <p className="mt-3 text-xs leading-relaxed text-muted-foreground">
                      Estimate only &mdash; educational, not a lending decision, pre-approval, or offer of credit.
                    </p>
                    {result.capped && (
                      <p className="mt-2 rounded-lg bg-muted/70 p-2 text-xs leading-relaxed text-foreground">
                        Capped at 64 because of a major credit event in the last three years.
                      </p>
                    )}
                  </CardContent>
                </Card>

                <Card>
                  <CardContent className="space-y-3 p-5">
                    <p className="text-sm font-bold text-foreground">See what improves your score fastest</p>
                    <p className="text-xs leading-relaxed text-muted-foreground">
                      No pressure and no obligation. A specialist can tell you which factor is actually blocking
                      approval for the loan programs you would use.
                    </p>
                    <Button asChild className="w-full" onClick={() => handleCtaClick("improve_plan")}>
                      <Link to="/auth?returnTo=/dashboard">
                        See my improvement plan
                        <ArrowRight className="ml-2 h-4 w-4" aria-hidden />
                      </Link>
                    </Button>
                    <Button
                      asChild
                      variant="outline"
                      className="w-full"
                      onClick={() => handleCtaClick("talk_to_specialist")}
                    >
                      <Link to="/experts">
                        <MessageCircle className="mr-2 h-4 w-4" aria-hidden />
                        Talk to a home loan specialist
                      </Link>
                    </Button>
                  </CardContent>
                </Card>
              </div>
            </div>

            <Card className="mt-6">
              <CardContent className="p-5">
                <h3 className="mb-4 font-bold text-foreground">Where your points are coming from</h3>
                <ul className="space-y-4">
                  {result.factors.map((f) => (
                    <li key={f.id}>
                      <div className="mb-1 flex flex-wrap items-baseline justify-between gap-2">
                        <p className="text-sm font-semibold text-foreground">
                          {f.label}{" "}
                          <span className="font-normal text-muted-foreground">&mdash; {f.detail}</span>
                        </p>
                        <p className="text-sm font-bold text-primary">
                          {f.points} / {f.weight}
                        </p>
                      </div>
                      <div className="mb-2 h-2 overflow-hidden rounded-full bg-muted">
                        <div
                          className="h-full rounded-full bg-primary"
                          style={{ width: `${(f.points / f.weight) * 100}%` }}
                        />
                      </div>
                      <p className="text-xs leading-relaxed text-muted-foreground">{f.tip}</p>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          </section>

          <section id="methodology" className="mb-12 scroll-mt-24">
            <h2 className="mb-3 text-2xl font-bold text-foreground">Methodology and limits</h2>
            <Card>
              <CardContent className="space-y-3 p-5 text-sm leading-relaxed text-muted-foreground">
                <p>
                  The weights on this page were chosen to reflect the relative emphasis lenders place on each factor
                  in general underwriting, and they are shown openly so the math is inspectable. They are not derived
                  from any lender's underwriting engine, are not endorsed by any agency or investor, and are not an
                  industry standard.
                </p>
                <p>
                  Everything is self-reported. No credit report is pulled, no income or assets are verified, and
                  nothing here is transmitted or stored. Thresholds commonly cited in the market &mdash; a 620
                  conventional floor, a 43% qualifying DTI, 20% down to avoid mortgage insurance &mdash; are general
                  reference points that vary by lender overlay, loan program, occupancy, property type, and state.
                </p>
                <p>
                  A high score here does not mean you will be approved, and a low score does not mean you will be
                  declined. Only a lender reviewing verified documents can make that determination.
                </p>
              </CardContent>
            </Card>
          </section>

          <section id="faq" className="mb-12 scroll-mt-24">
            <h2 className="mb-4 text-2xl font-bold text-foreground">Common questions</h2>
            <Accordion
              type="single"
              collapsible
              value={openFaq}
              onValueChange={handleFaqChange}
              className="rounded-xl border border-border bg-card px-4 [&_[role=region][data-state=closed]]:hidden"
            >
              {FAQS.map((faq) => (
                <AccordionItem key={faq.id} value={faq.id}>
                  <AccordionTrigger className="text-left text-base font-semibold">{faq.q}</AccordionTrigger>
                  <AccordionContent
                    forceMount
                    className="text-sm leading-relaxed text-muted-foreground"
                  >
                    {faq.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </section>

          <section className="mb-6 print:hidden">
            <h2 className="mb-3 text-lg font-bold text-foreground">Keep going</h2>
            <div className="flex flex-wrap gap-3 text-sm">
              <Link to="/tools/raise-credit-score-before-mortgage" className="text-primary hover:underline">
                Raise your score before applying
              </Link>
              <span className="text-muted-foreground">&middot;</span>
              <Link to="/tools/home-buying-timeline" className="text-primary hover:underline">
                The full home buying timeline
              </Link>
              <span className="text-muted-foreground">&middot;</span>
              <Link to="/tools/compare-lender-quotes" className="text-primary hover:underline">
                Compare lender quotes
              </Link>
              <span className="text-muted-foreground">&middot;</span>
              <Link to="/tools/loan-officer-questions" className="text-primary hover:underline">
                Questions to ask a loan officer
              </Link>
              <span className="text-muted-foreground">&middot;</span>
              <Link to="/tools/real-affordability" className="text-primary hover:underline">
                What payment can I actually afford?
              </Link>
              <Link to="/tools/mortgage-ready-checklist" className="text-primary hover:underline">
                Work through the mortgage-ready checklist
              </Link>
              <span className="text-muted-foreground">&middot;</span>
              <Link to="/tools/free-homebuying-readiness-tools" className="text-primary hover:underline">
                Every free tool for checking your readiness
              </Link>
            </div>
          </section>

          <p className="text-xs text-muted-foreground">
            Educational information only &mdash; not legal, financial, or tax advice, not a credit decision, and not
            an offer of credit or a commitment to lend. Scores produced here are estimates based on numbers you
            enter and are not used by any lender. Qualification standards vary by lender, loan program, and state.
          </p>
        </div>
      </main>

      <RelatedTools currentPath="/tools/homebuyer-readiness-score" />
      <Footer />
    </div>
  );
};

export default HomebuyerReadinessScore;
